// A small typed client for the API. Works in the browser (cookie sessions) and in a native app
// (bearer tokens). It only needs fetch, FormData and Blob, which browsers and React Native provide.
import type { z } from 'zod';
import type {
  ActivityCreate, ActivityPatch, CalendarQueryInput, CheckCreate, ReminderCreate, ReminderPatch, ScreenSummary,
  SpaceBody, SpaceCreate, ContactBody, TrackBody, UserCreateBody, UserPatchBody, UserPrefsPatch, FolderCreate, FolderPatch, SaveToFolderBody, SeenKind,
} from './schemas.js';
import type {
  QuickParseResult, VoiceIndex,
  Activity, ActivityDetail, AppNotification, CalendarItem, Contact, Conversation, ConversationDetail, Device, FileItem,
  LoginResponse, MessageResult, NotificationList, ProposedAction, Reminder, SessionUser, SpaceDetail, SpaceSummary, ArchivedList, DeletedList,
  Summary, CheckView, TeamCheck, TrackDetail, User, ApiErrorBody, Page, SessionInfo, ResetLink, CreatedUser, Health,
  ContextDoc, ContextHistoryEntry, ReminderDraft, Teammate, Transcript, UserPrefs, Starter, Brief, BriefKind, TrackOverview, TodayData, TrackUpdate,
  FileDetail, FileSummary, Folder, FolderAnswer, FolderDetail, NameConflict, SearchHit, UploadTicket, SearchAnswer, SearchKind, SearchResults, EmailStatus, SavedSearch, MeetingRow, MeetingDetail, Recording, UsageReport, TodayCards, MorningEmailStatus,
  Insight, ContestedPair, Follow, ResearchTask, ResearchRun, ResearchTopic, ResearchOverview, PersonSummary, NewSince,
} from './types.js';
import type { ConversationPurpose } from './enums.js';
import type { FollowFrequency, InsightCategory, InsightConfidence, ResearchLevel } from './enums.js';
import type { ActivityType, ContextSection, ContextTargetKind, DevicePlatform, NotificationKind, SessionClient } from './enums.js';

export type AuthStrategy =
  | { kind: 'cookie' }
  | { kind: 'bearer'; getToken: () => string | null | Promise<string | null> };

export interface ApiClientOptions {
  /** e.g. "" for the same origin, or "https://workspace.deltapartners-me.com". */
  baseUrl: string;
  auth: AuthStrategy;
  fetch?: typeof fetch;
  /** Called on any 401, e.g. to show the sign-in screen. */
  onUnauthorized?: () => void;
}

export class ApiError extends Error {
  /** conflict: on a 409 name_conflict (Knowledge base upload), the file already there and the choices. */
  constructor(public status: number, public code: string, message: string, public current?: unknown, public conflict?: NameConflict) { super(message); }
}

type Query = Record<string, string | boolean | undefined | null>;

export function createApiClient(opts: ApiClientOptions) {
  const f = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = opts.baseUrl.replace(/\/$/, '');

  async function headers(json: boolean): Promise<Record<string, string>> {
    const h: Record<string, string> = { accept: 'application/json' };
    if (json) h['content-type'] = 'application/json';
    if (opts.auth.kind === 'bearer') {
      const t = await opts.auth.getToken();
      if (t) h.authorization = `Bearer ${t}`;
    } else {
      // Cookie sessions: this header proves the request came from our own scripts (CSRF protection).
      h['x-dpw-client'] = 'web';
    }
    return h;
  }

  function url(path: string, q?: Query) {
    const qs = q ? Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&') : '';
    return `${base}/api${path}${qs ? `?${qs}` : ''}`;
  }

  async function send(method: string, path: string, body?: unknown, q?: Query): Promise<Response> {
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    const res = await f(url(path, q), {
      method,
      headers: await headers(body !== undefined && !isForm),
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      credentials: opts.auth.kind === 'cookie' ? 'include' : 'omit',
    });
    if (!res.ok) {
      let code = 'error', message = `Request failed (${res.status})`, current: unknown, conflict: NameConflict | undefined;
      try {
        const b = (await res.json()) as Partial<ApiErrorBody>;
        if (b.error) { code = b.error.code; message = b.error.message; current = b.error.current; conflict = b.error.conflict; }
      } catch { /* not JSON */ }
      if (res.status === 401) opts.onUnauthorized?.();
      throw new ApiError(res.status, code, message, current, conflict);
    }
    return res;
  }

  async function json<T>(method: string, path: string, body?: unknown, q?: Query): Promise<T> {
    const res = await send(method, path, body, q);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
  const get = <T>(p: string, q?: Query) => json<T>('GET', p, undefined, q);
  const post = <T>(p: string, b: unknown = {}) => json<T>('POST', p, b);
  const patch = <T>(p: string, b: unknown) => json<T>('PATCH', p, b);
  const del = (p: string) => json<void>('DELETE', p);
  const put = <T>(p: string, b: unknown) => json<T>('PUT', p, b);

  /** Send the bytes for a ticket: a form POST straight to S3, or a PUT to the API (local storage). */
  async function sendUpload(t: UploadTicket, file: Blob): Promise<void> {
    if (t.method === 'POST') {
      const fd = new FormData();
      for (const [k, v] of Object.entries(t.fields)) fd.append(k, v);
      fd.append('file', file);
      const r = await f(t.url, { method: 'POST', body: fd });
      if (!r.ok) throw new ApiError(r.status, 'upload_failed', 'The file could not be sent to storage. Try again.');
      return;
    }
    const h = await headers(false);
    const r = await f(t.url.startsWith('/api/') ? `${base}${t.url}` : t.url, { method: 'PUT', headers: { ...h, 'content-type': 'application/octet-stream' }, body: file, credentials: opts.auth.kind === 'cookie' ? 'include' : 'omit' });
    if (!r.ok) throw new ApiError(r.status, 'upload_failed', 'The file could not be sent. Try again.');
  }

  /**
   * A message whose reply streams in: onText gets the words as the assistant writes them, the promise the whole result
   * (the same as send). Falls back to a plain send where the response body cannot be read as a stream.
   */
  async function sendStreaming(id: string, content: string, o: { spoken?: boolean; screen?: ScreenSummary }, onText: (delta: string) => void): Promise<MessageResult> {
    const res = await send('POST', `/conversations/${id}/messages/stream`, { content, ...(o.spoken ? { spoken: true } : {}), ...(o.screen ? { screen: o.screen } : {}) });
    const reader = res.body?.getReader();
    if (!reader) throw new ApiError(500, 'no_stream', 'The reply could not be read. Try again.');
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (value) buf += dec.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, nl).trim();
        buf = buf.slice(nl + 1);
        if (!line) continue;
        const m = JSON.parse(line) as { t: 'text'; d: string } | { t: 'done'; r: MessageResult } | { t: 'error'; status: number; code: string; message: string };
        if (m.t === 'text') onText(m.d);
        else if (m.t === 'done') return m.r;
        else throw new ApiError(m.status, m.code, m.message);
      }
      if (done) throw new ApiError(502, 'stream_cut', 'The reply was cut off. Try again.');
    }
  }

  function form(file: Blob, name: string) {
    const fd = new FormData();
    fd.append('file', file, name);
    return fd;
  }

  return {
    auth: {
      login: (email: string, password: string, client: SessionClient = opts.auth.kind === 'bearer' ? 'app' : 'web') =>
        post<LoginResponse>('/auth/login', { email, password, client }),
      logout: () => post<void>('/auth/logout'),
      me: () => get<{ user: SessionUser }>('/auth/me'),
      changePassword: (current: string, next: string) => post<void>('/auth/password', { current, next }),
      /** Set a password from a one-time link (no session needed). */
      setPassword: (token: string, password: string) => post<void>('/auth/set-password', { token, password }),
      sessions: () => get<SessionInfo[]>('/auth/sessions'),
      revokeSession: (id: string) => del(`/auth/sessions/${id}`),
    },
    health: () => get<Health>('/health'),
    summary: () => get<Summary>('/summary'),
    /** The blue "new" dots: what other people added since you looked, and saying you looked. */
    seen: {
      get: () => get<NewSince>('/seen'),
      mark: (kind: SeenKind, id: string | null = null) => post<void>('/seen', { kind, id }),
    },
    users: {
      list: () => get<User[]>('/users'),
      create: (b: z.input<typeof UserCreateBody>) => post<CreatedUser>('/users', b),
      update: (id: string, b: z.input<typeof UserPatchBody>) => patch<User>(`/users/${id}`, b),
      /** Admin: a one-time link the person uses to set a new password. */
      resetPassword: (id: string) => post<ResetLink>(`/users/${id}/reset-password`),
      /** People you may set a reminder for (everyone who shares a space with you). */
      teammates: () => get<Teammate[]>('/users/teammates'),
    },
    spaces: {
      list: () => get<SpaceSummary[]>('/spaces'),
      get: (id: string) => get<SpaceDetail>(`/spaces/${id}`),
      create: (b: z.input<typeof SpaceCreate>) => post<SpaceDetail>('/spaces', b),
      update: (id: string, b: Partial<z.input<typeof SpaceBody>>) => patch<SpaceDetail>(`/spaces/${id}`, b),
      addMember: (id: string, userId: string, role: 'lead' | 'member' = 'member') => post<void>(`/spaces/${id}/members`, { userId, role }),
      removeMember: (id: string, userId: string) => del(`/spaces/${id}/members/${userId}`),
      addContact: (id: string, b: z.input<typeof ContactBody>) => post<Contact>(`/spaces/${id}/contacts`, b),
      activities: (id: string, q: { trackId?: string; type?: ActivityType; mine?: boolean; includeDone?: boolean; clientRequests?: boolean; limit?: number; cursor?: string } = {}) =>
        get<Page<Activity>>(`/spaces/${id}/activities`, { ...q, limit: q.limit ? String(q.limit) : undefined }),
      createTrack: (id: string, b: z.input<typeof TrackBody>) => post<TrackDetail>(`/spaces/${id}/tracks`, b),
      /** Out of every list and page until restored; nothing is deleted. Admins and the director. */
      archive: (id: string) => post<void>(`/spaces/${id}/archive`),
      restore: (id: string) => post<void>(`/spaces/${id}/restore`),
      /** Natural-language quick add: the model's reading of the line (ids only from this space). Creates nothing. */
      quickParse: (id: string, b: { text: string; trackId?: string | null }) => post<QuickParseResult>(`/spaces/${id}/quick-add/parse`, b),
    },
    /** Settings: what is archived (Restore), and activities deleted in the last 30 days (Restore). */
    archived: () => get<ArchivedList>('/archived'),
    deleted: () => get<DeletedList>('/deleted'),
    contacts: {
      update: (id: string, b: Partial<z.input<typeof ContactBody>>) => patch<Contact>(`/contacts/${id}`, b),
      remove: (id: string) => del(`/contacts/${id}`),
    },
    tracks: {
      get: (id: string) => get<TrackDetail>(`/tracks/${id}`),
      update: (id: string, b: Partial<z.input<typeof TrackBody>>) => patch<TrackDetail>(`/tracks/${id}`, b),
      activities: (id: string, q: { includeDone?: boolean; type?: ActivityType; mine?: boolean; limit?: number; cursor?: string } = {}) =>
        get<Page<Activity>>(`/tracks/${id}/activities`, { ...q, limit: q.limit ? String(q.limit) : undefined }),
      quickAdd: (id: string, title: string) => post<Activity>(`/tracks/${id}/quick-add`, { title }),
      files: (id: string) => get<FileItem[]>(`/tracks/${id}/files`),
      upload: (id: string, file: Blob, name: string) => post<FileItem>(`/tracks/${id}/files`, form(file, name)),
      archive: (id: string) => post<{ spaceId: string }>(`/tracks/${id}/archive`),
      restore: (id: string) => post<void>(`/tracks/${id}/restore`),
    },
    activities: {
      /** Every activity you can see (for pickers); q searches titles. */
      list: (q: { q?: string; includeDone?: boolean; limit?: number; cursor?: string } = {}) =>
        get<Page<Activity>>('/activities', { ...q, limit: q.limit ? String(q.limit) : undefined }),
      get: (id: string) => get<ActivityDetail>(`/activities/${id}`),
      create: (b: ActivityCreate) => post<Activity>('/activities', b),
      /** version: the version you edited; a newer one on the server gives a 409 with the current record. */
      update: (id: string, b: ActivityPatch, version: number) => patch<Activity>(`/activities/${id}`, { ...b, version }),
      remove: (id: string) => del(`/activities/${id}`),
      restore: (id: string) => post<Activity>(`/activities/${id}/restore`),
      addStep: (id: string, title: string, ownerId?: string | null) => post<{ id: string }>(`/activities/${id}/steps`, { title, ownerId }),
      updateStep: (stepId: string, b: { title?: string; ownerId?: string | null; done?: boolean }) => patch<void>(`/steps/${stepId}`, b),
      deleteStep: (stepId: string) => del(`/steps/${stepId}`),
      reorderSteps: (id: string, stepIds: string[]) => post<void>(`/activities/${id}/steps/reorder`, { stepIds }),
      addUpdate: (id: string, body: string) => post<{ id: string }>(`/activities/${id}/updates`, { body }),
      files: (id: string) => get<FileItem[]>(`/activities/${id}/files`),
      upload: (id: string, file: Blob, name: string) => post<FileItem>(`/activities/${id}/files`, form(file, name)),
    },
    files: {
      /** Downloads through the API so the session is checked. */
      download: async (id: string) => (await send('GET', `/files/${id}/download`)).blob(),
      downloadUrl: (id: string) => url(`/files/${id}/download`),
      remove: (id: string) => del(`/files/${id}`),
    },
    reminders: {
      /** mine: reminders for me. set_by_me: ones I set for other people. */
      list: (scope: 'mine' | 'set_by_me' = 'mine') => get<Reminder[]>('/reminders', { scope }),
      /** Words (typed, or a transcript) to form fields. Saves nothing: the person reviews and saves. */
      parse: (text: string) => post<ReminderDraft>('/reminders/parse', { text }),
      create: (b: ReminderCreate) => post<Reminder>('/reminders', b),
      update: (id: string, b: Omit<ReminderPatch, 'version'>, version: number) => patch<Reminder>(`/reminders/${id}`, { ...b, version }),
      done: (id: string) => post<Reminder>(`/reminders/${id}/done`),
      undo: (id: string) => post<Reminder>(`/reminders/${id}/undo`),
      remove: (id: string) => del(`/reminders/${id}`),
    },
    notifications: {
      list: (cursor?: string) => get<NotificationList>('/notifications', { cursor }),
      dismiss: (id: string) => post<void>(`/notifications/${id}/dismiss`),
      /** Undo a Dismiss. */
      restore: (id: string) => post<void>(`/notifications/${id}/restore`),
      /** Dismiss every open row, or one kind's; returns the ids for Undo. */
      dismissAll: (kind?: NotificationKind) => post<{ ids: string[] }>('/notifications/dismiss-all', kind ? { kind } : {}),
      /** Undo a Dismiss all. */
      restoreMany: (ids: string[]) => post<void>('/notifications/restore', { ids }),
    },
    /** The assistant's brief on a space, a track or an activity. */
    brief: (kind: BriefKind, id: string) => get<Brief>(kind === 'day' ? '/today/brief' : `/${kind === 'space' ? 'spaces' : kind === 'track' ? 'tracks' : 'activities'}/${id}/brief`),
    trackOverview: (id: string) => get<TrackOverview>(`/tracks/${id}/overview`),
    today: {
      get: () => get<TodayData>('/today'),
      /** The four cards on top (desktop): due this week, meetings, awaiting approval, knowledge base. */
      cards: () => get<TodayCards>('/today/cards'),
      /** "Your day": two sentences on what matters today. */
      brief: () => get<Brief>('/today/brief'),
      /** "It is fine": hide a gone-quiet activity or track from your Today for 7 days; unhide undoes it. */
      hide: (kind: 'activity' | 'track', id: string) => post<void>('/today/hidden', { kind, id }),
      unhide: (kind: 'activity' | 'track', id: string) => del(`/today/hidden/${kind}/${id}`),
    },
    trackUpdates: (id: string, kind: 'all' | 'minutes' | 'email' = 'all', cursor?: string) => get<Page<TrackUpdate>>(`/tracks/${id}/updates`, { kind, cursor }),
    prefs: {
      get: () => get<UserPrefs>('/me/prefs'),
      update: (b: z.input<typeof UserPrefsPatch>) => patch<UserPrefs>('/me/prefs', b),
    },
    actions: {
      /** forWhom everyone: every person's cards (the director only). */
      list: (status: 'waiting' | 'all' = 'waiting', cursor?: string, forWhom: 'me' | 'everyone' = 'me') => get<Page<ProposedAction>>('/actions', { status, cursor, for: forWhom }),
      get: (id: string) => get<ProposedAction>(`/actions/${id}`),
      change: (id: string, text: string) => post<ProposedAction>(`/actions/${id}/change`, { text }),
      approve: (id: string, version?: number) => post<ProposedAction>(`/actions/${id}/approve`, { version }),
      /** Clears the card everywhere without doing it. Not available on cards that settle a team check. */
      ignore: (id: string) => post<ProposedAction>(`/actions/${id}/ignore`),
      /** Forwarded email cards: Space and track, and Log as (an activity there, or null for a new activity). */
      retarget: (id: string, b: { trackId: string; activityId: string | null }) => post<ProposedAction>(`/actions/${id}/retarget`, b),
      /** A forwarded email's card: leave a part out (include false) or put it back. */
      extra: (id: string, extraId: string, include: boolean) => post<ProposedAction>(`/actions/${id}/extras`, { id: extraId, include }),
      /** Undo an Ignore (the person who ignored it, for a few minutes). */
      restore: (id: string) => post<ProposedAction>(`/actions/${id}/restore`),
    },
    calendar: (q: CalendarQueryInput) => get<CalendarItem[]>('/calendar', { from: q.from, to: q.to, spaceId: q.spaceId, mine: q.mine }),
    assistant: {
      conversations: (cursor?: string) => get<Page<Conversation>>('/conversations', { cursor }),
      create: (b: { title?: string | null; spaceId?: string | null; trackId?: string | null; activityId?: string | null; folderId?: string | null; fileId?: string | null; purpose?: ConversationPurpose | null } = {}) => post<Conversation>('/conversations', b),
      get: (id: string) => get<ConversationDetail>(`/conversations/${id}`),
      setSpace: (id: string, spaceId: string | null) => patch<void>(`/conversations/${id}`, { spaceId }),
      /** spoken: the words were said in Talk (the reply is read out; the assistant answers in a few spoken sentences). */
      send: (id: string, content: string, o: { spoken?: boolean } = {}) => post<MessageResult>(`/conversations/${id}/messages`, { content, ...(o.spoken ? { spoken: true } : {}) }),
      /** The same as send, with the reply's words as they are written (onText). */
      sendStreaming: (id: string, content: string, onText: (delta: string) => void, o: { spoken?: boolean; screen?: ScreenSummary } = {}) => sendStreaming(id, content, o, onText),
      attach: (id: string, file: Blob, name: string) => post<FileItem>(`/conversations/${id}/files`, form(file, name)),
      describe: (b: { text: string; spaceId: string; trackId?: string | null }) => post<MessageResult>('/assistant/describe', b),
      /** "Try saying" for a new conversation, made from the person's own work. */
      starters: (spaceId?: string) => get<Starter[]>('/assistant/starters', { spaceId }),
      /** Ask about one activity ("Tell the AI"): a conversation about it, the reply and any card come back. */
      askActivity: (activityId: string, text: string) => post<MessageResult>(`/activities/${activityId}/ask`, { content: text }),
    },
    checks: {
      list: (scope: 'asked' | 'to_answer' | 'all' = 'all', cursor?: string) => get<Page<CheckView>>('/checks', { scope, cursor }),
      get: (id: string) => get<CheckView>(`/checks/${id}`),
      create: (b: CheckCreate) => post<CheckView>('/checks', b),
      answer: (id: string, answer: string) => post<TeamCheck>(`/checks/${id}/answers`, { answer }),
      /** Asker confirms (no review), or the director approves (review). */
      confirm: (id: string, b: { answerId?: string; text?: string }) => post<TeamCheck>(`/checks/${id}/confirm`, b),
      divert: (id: string, userIds: string[]) => post<TeamCheck>(`/checks/${id}/divert`, { userIds }),
      sendBack: (id: string, question: string, userIds?: string[]) => post<TeamCheck>(`/checks/${id}/send-back`, { question, userIds }),
    },
    /** Space or Track context: read, save one section against the version you edited, history. */
    context: {
      get: (kind: ContextTargetKind, id: string) => get<ContextDoc>(`/${kind === 'space' ? 'spaces' : 'tracks'}/${id}/context`),
      save: (kind: ContextTargetKind, id: string, section: ContextSection, text: string, version: number) =>
        patch<ContextDoc>(`/${kind === 'space' ? 'spaces' : 'tracks'}/${id}/context`, { section, text, version }),
      history: (kind: ContextTargetKind, id: string, cursor?: string) =>
        get<Page<ContextHistoryEntry>>(`/${kind === 'space' ? 'spaces' : 'tracks'}/${id}/context/history`, { cursor }),
    },
    /** Knowledge base: folders, uploads (presigned; a same-name file is an ApiError name_conflict with .conflict), files, search. */
    knowledge: {
      folders: (q: { spaceId?: string; teamWide?: boolean } = {}) => get<Folder[]>('/folders', q),
      folder: (id: string) => get<FolderDetail>(`/folders/${id}`),
      createFolder: (b: FolderCreate) => post<Folder>('/folders', b),
      updateFolder: (id: string, b: FolderPatch) => patch<Folder>(`/folders/${id}`, b),
      deleteFolder: (id: string) => json<{ unlinked: number; removed: number }>('DELETE', `/folders/${id}`),
      /** Upload into a folder. resolve answers a name_conflict: add as the next version, or keep both. */
      upload: async (folderId: string, file: Blob, name: string, resolve?: 'version' | 'keep_both') => {
        const t = await post<UploadTicket>(`/folders/${folderId}/uploads`, { name, size: file.size, mime: file.type, resolve });
        await sendUpload(t, file);
        return post<FileItem>(`/uploads/${t.uploadId}/complete`);
      },
      uploadVersion: async (fileId: string, file: Blob, name: string) => {
        const t = await post<UploadTicket>(`/files/${fileId}/versions/uploads`, { name, size: file.size, mime: file.type });
        await sendUpload(t, file);
        return post<FileItem>(`/uploads/${t.uploadId}/complete`);
      },
      file: (id: string) => get<FileDetail>(`/files/${id}`),
      versionUrl: (id: string, version: number) => url(`/files/${id}/versions/${version}/download`, { inline: '1' }),
      restoreVersion: (id: string, version: number) => post<FileItem>(`/files/${id}/versions/${version}/restore`),
      editSummary: (id: string, summary: FileSummary) => put<FileDetail>(`/files/${id}/summary`, { summary }),
      /** A person edited it: the first call is an ApiError confirm_rewrite; ask, then call again with confirm. */
      rewriteSummary: (id: string, confirm = false) => post<{ queued: true }>(`/files/${id}/summary/rewrite`, { confirm }),
      addToFolder: (fileId: string, folderId: string) => post<FileItem>(`/files/${fileId}/folders`, { folderId }),
      removeFromFolder: (fileId: string, folderId: string) => del(`/files/${fileId}/folders/${folderId}`),
      /** Research Center: keep an insight or a report in a folder. */
      save: (folderId: string, b: SaveToFolderBody) => post<FileItem>(`/folders/${folderId}/save`, b),
      search: (q: string, spaceId?: string) => get<{ items: SearchHit[] }>('/search', { q, spaceId }),
      /** Ask this folder: an answer from its files only, with file and page sources. */
      ask: (folderId: string, question: string) => post<FolderAnswer>(`/folders/${folderId}/ask`, { question }),
      /** The start of a presigned upload (the web sends the bytes itself, to show progress). */
      startUpload: (folderId: string, b: { name: string; size: number; mime: string; resolve?: 'version' | 'keep_both' }) => post<UploadTicket>(`/folders/${folderId}/uploads`, b),
      startVersionUpload: (fileId: string, b: { name: string; size: number; mime: string }) => post<UploadTicket>(`/files/${fileId}/versions/uploads`, b),
      completeUpload: (uploadId: string) => post<FileItem>(`/uploads/${uploadId}/complete`),
      openUrl: (fileId: string) => url(`/files/${fileId}/download`, { inline: '1' }),
    },
    /** Speech to text (a recording from MediaRecorder or the phone). Then pass the text to reminders.parse. */
    transcribe: (audio: Blob, name = 'recording.webm') => post<Transcript>('/transcribe', form(audio, name)),
    /** Email on this server: sending and receiving connected or not, the inbound address, and (admins) what failed. */
    email: { status: () => get<EmailStatus>('/email') },
    /** Is voice (ElevenLabs) connected on this server: Record and the Speak button need it. */
    /** The app is open and in use this minute (Usage counts active minutes; people never see them). */
    presence: () => post<void>('/presence', {}),
    /** Settings, Usage: admins only. */
    usage: (q: { range?: 'week' | '4weeks' | 'quarter'; userId?: string } = {}) => get<UsageReport>('/usage', q),
    morningEmail: {
      status: () => get<MorningEmailStatus>('/morning-email'),
      /** Admins: queue your own morning email now (the daily one still goes). */
      sendMe: () => post<{ queued: boolean; subject: string }>('/morning-email/send-me'),
    },
    voice: {
      status: () => get<{ transcribe: boolean; speak: boolean }>('/voice'),
      /** Voice control: every name it can open (what you can see), with where each opens. */
      index: () => get<VoiceIndex>('/voice/index'),
      /** The words as audio (MP3 from ElevenLabs; a short tone from the fake). */
      speak: async (text: string) => { const r = await send('POST', '/voice/speak', { text }); return { audio: await r.blob(), demo: r.headers.get('x-dpw-demo') === '1' }; },
    },
    /** Research Center: insights, research tasks at a level, followed links, topics, the runs and the overview. */
    research: {
      overview: () => get<ResearchOverview>('/research'),
      newCount: () => get<{ n: number }>('/research/new-count'),
      insights: (q: { category?: InsightCategory; status?: 'new' | 'kept' | 'all'; source?: string; q?: string } = {}) => get<Insight[]>('/insights', q),
      insight: (id: string) => get<Insight>(`/insights/${id}`),
      addInsight: (b: { url?: string | null; text?: string | null; why?: string | null; category?: InsightCategory | null }) => post<Insight>('/insights', b),
      patchInsight: (id: string, b: { title?: string; category?: InsightCategory; summary?: string; why?: string; trackIds?: string[]; tags?: string[]; confidence?: InsightConfidence | null }) => patch<Insight>(`/insights/${id}`, b),
      /** Contested: open pairs; mark two as contradicting; resolve (keep one, or both with a note). */
      contested: () => get<ContestedPair[]>('/research/contested'),
      contradicts: (id: string, otherId: string, note?: string | null) => post<ContestedPair>(`/insights/${id}/contradicts`, { otherId, note }),
      resolve: (pairId: string, b: { keepId: string; note?: string | null } | { keepBoth: true; note: string }) => post<{ ok: true }>(`/research/contested/${pairId}/resolve`, b),
      /** 409 check_failed: send again with anyway: true ("Keep anyway"). */
      decide: (id: string, decision: 'keep' | 'dismiss' | 'undo', anyway?: boolean) => post<Insight>(`/insights/${id}/decide`, { decision, anyway }),
      saveInsight: (id: string, folderId: string) => post<FileItem>(`/insights/${id}/save`, { folderId }),
      tasks: () => get<ResearchTask[]>('/research/tasks'),
      task: (id: string) => get<ResearchTask>(`/research/tasks/${id}`),
      createTask: (b: { question: string; level: ResearchLevel }) => post<ResearchTask>('/research/tasks', b),
      answerTask: (id: string, answer: string) => post<ResearchTask>(`/research/tasks/${id}/answer`, { answer }),
      runs: () => get<ResearchRun[]>('/research/runs'),
      follows: () => get<Follow[]>('/follows'),
      follow: (id: string) => get<Follow>(`/follows/${id}`),
      addFollow: (b: { url: string; name?: string | null; watchFor?: string | null; frequency?: FollowFrequency }) => post<Follow>('/follows', b),
      patchFollow: (id: string, b: { watchFor?: string | null; frequency?: FollowFrequency; name?: string }) => patch<Follow>(`/follows/${id}`, b),
      checkNow: (id: string) => post<Follow>(`/follows/${id}/check`, {}),
      stopFollowing: (id: string) => del(`/follows/${id}`),
      topics: () => get<ResearchTopic[]>('/research/topics'),
      addTopic: (text: string) => post<ResearchTopic[]>('/research/topics', { text }),
      removeTopic: (id: string) => json<ResearchTopic[]>('DELETE', `/research/topics/${id}`),
      setLead: (userId: string | null) => put<PersonSummary | null>('/research/lead', { userId }),
    },
    /** Meetings: the list, a meeting's page, Ask about it, and log a meeting by hand (saved directly). */
    meetings: {
      list: (q: { when?: 'all' | 'upcoming' | 'past'; spaceId?: string } = {}) => get<MeetingRow[]>('/meetings', q),
      get: (id: string) => get<MeetingDetail>(`/meetings/${id}`),
      ask: (id: string, question: string) => post<{ answer: string; at: number | null }>(`/meetings/${id}/ask`, { question }),
      log: (b: { activityId?: string | null; trackId?: string | null; title?: string | null; startsAt?: string | null; endsAt?: string | null; minutes: string;
        actions?: { title: string; ownerIds?: string[]; dueDate?: string | null }[] }) => post<{ activityId: string; createdActivityIds: string[] }>('/meetings/log', b),
    },
    /**
     * Recording a meeting: start, then each part (a whole audio file of about partSeconds) while it runs, the whole
     * recording when it stops, then stop; the minutes follow as a card. Discard throws a running one away.
     */
    recordings: {
      start: (b: { activityId?: string | null; trackId?: string | null; title?: string | null }) => post<Recording>('/recordings', b),
      get: (id: string) => get<Recording>(`/recordings/${id}`),
      part: (id: string, seq: number, startSec: number, audio: Blob, name = `part-${seq}.webm`) => {
        const fd = new FormData();
        fd.append('startSec', String(startSec));
        fd.append('file', audio, name);
        return json<void>('POST', `/recordings/${id}/parts/${seq}`, fd);
      },
      audio: (id: string, audio: Blob, name = 'meeting.webm') => json<void>('PUT', `/recordings/${id}/audio`, form(audio, name)),
      stop: (id: string, b: { durationSecs: number; marks?: number[] }) => post<Recording>(`/recordings/${id}/stop`, b),
      discard: (id: string) => del(`/recordings/${id}`),
      /** Name a voice ("Speaker 2" is Sara Al-Amri): applies to the whole transcript. */
      nameSpeaker: (id: string, label: string, name: string) => put<Recording>(`/recordings/${id}/speakers`, { label, name }),
      /** Where the player reads the whole recording from. */
      audioUrl: (id: string) => url(`/recordings/${id}/audio`),
    },
    /** The Search page: everything you can open, by kind and tag; and the assistant's answer to the same words. */
    search: {
      all: (q: string, o: { kind?: SearchKind | undefined; tags?: string[] | undefined } = {}) =>
        get<SearchResults>('/search/all', { q, kind: o.kind, tags: o.tags?.length ? o.tags.join(',') : undefined }),
      /** Starts a conversation with the question (Continue in the assistant opens it). */
      ask: (q: string) => post<SearchAnswer>('/search/ask', { q }),
      /** Saved filters: personal, each with its live count. */
      saved: () => get<SavedSearch[]>('/search/saved'),
      save: (b: { name?: string; q: string; kind?: SearchKind | null; tags?: string[] }) => post<SavedSearch>('/search/saved', b),
      renameSaved: (id: string, name: string) => patch<SavedSearch>(`/search/saved/${id}`, { name }),
      deleteSaved: (id: string) => del(`/search/saved/${id}`),
    },
    devices: {
      register: (platform: DevicePlatform, token: string) => post<Device>('/devices', { platform, token }),
      remove: (id: string) => del(`/devices/${id}`),
      /** Turn push off in this browser (by its subscription). */
      forget: (platform: DevicePlatform, token: string) => json<void>('POST', '/devices/forget', { platform, token }),
    },
    /** Web push: the server's public key to subscribe with, or why push is off. */
    push: {
      status: () => get<{ publicKey: string | null; problem: string | null }>('/push'),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
export type { AppNotification };
